import { Link } from 'react-router-dom';
import { text } from 'stream/consumers';

type ContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'group'; title: string; blocks: ContentBlock[] };

const intro = [
  'This Privacy Notice explains how AppGallop Pvt. Ltd. ("AppGallop", "we", "our", or "us") collects, uses, stores, and shares information when you interact with our website, applications, products, and services, including the AppGallop Marketplace (collectively, the "Services").',
  'We are committed to protecting your privacy and ensuring transparency regarding how your information is handled. By using our Services, you agree to the practices described in this Privacy Notice.',
  'This Notice does not govern the privacy practices of third-party developers, syndication partners, or external service providers who may offer services through the AppGallop Marketplace. We encourage you to review their privacy policies separately.',
];

const sections: { title: string; blocks: ContentBlock[] }[] = [
  {
    title: '1. Information We Collect',
    blocks: [
      {
        type: 'group',
        title: 'Information You Provide Directly',
        blocks: [
          {
            type: 'group',
            title: 'Account Registration & Profile Information',
            blocks: [
              { type: 'paragraph', text: 'When you create an account, we may collect:' },
              {
                type: 'list',
                items: [
                  'Full name',
                  'Email address',
                  'Job title',
                  'Phone number',
                  'Date of birth',
                  'Social media handles',
                  'Professional experience',
                  'Profile information you voluntarily provide',
                ],
              },
            ],
          },
          {
            type: 'group',
            title: 'User Content',
            blocks: [
              { type: 'paragraph', text: 'We collect information and content you submit through the Services, including:' },
              {
                type: 'list',
                items: ['Comments', 'Reviews', 'Messages', 'Documents', 'Images', 'Media files', 'Other user-generated content'],
              },
            ],
          },
          {
            type: 'group',
            title: 'Connected Third-Party Services',
            blocks: [
              {
                type: 'paragraph',
                text: 'When you connect AppGallop with third-party cloud services, you authorize us to access and process information stored within those services to provide functionality such as:',
              },
              { type: 'list', items: ['Search', 'Analytics', 'Collaboration', 'Productivity tools'] },
              { type: 'paragraph', text: 'You may disconnect these integrations at any time.' },
            ],
          },
          {
            type: 'group',
            title: 'Communications',
            blocks: [
              { type: 'paragraph', text: 'We collect information provided through:' },
              {
                type: 'list',
                items: [
                  'Customer support interactions',
                  'Feedback submissions',
                  'Surveys',
                  'Communications with our team',
                  'Interactions with other users through the Services',
                ],
              },
            ],
          },
        ],
      },
      {
        type: 'group',
        title: 'Information Collected Automatically',
        blocks: [
          {
            type: 'group',
            title: 'Device & Usage Information',
            blocks: [
              { type: 'paragraph', text: 'When you access our Services, we may automatically collect:' },
              {
                type: 'list',
                items: [
                  'IP address',
                  'Device identifiers',
                  'Browser type',
                  'Operating system',
                  'Pages visited',
                  'Referring websites',
                  'Date and time of access',
                  'Usage patterns and activity logs',
                ],
              },
            ],
          },
          {
            type: 'group',
            title: 'Cookies and Similar Technologies',
            blocks: [
              { type: 'paragraph', text: 'We use cookies and similar technologies to:' },
              {
                type: 'list',
                items: [
                  'Improve user experience',
                  'Remember preferences',
                  'Enable authentication',
                  'Analyze service performance',
                  'Measure website traffic',
                  'Enhance security',
                ],
              },
              {
                type: 'paragraph',
                text: 'You can manage cookie preferences through your browser settings. Disabling cookies may affect certain Service functionality.',
              },
            ],
          },
          {
            type: 'group',
            title: 'Analytics & Advertising Technologies',
            blocks: [
              {
                type: 'paragraph',
                text: 'We use analytics tools, including Google Analytics, to understand how users interact with our Services and improve performance.',
              },
              { type: 'paragraph', text: 'These tools may collect:' },
              { type: 'list', items: ['IP addresses', 'Browser information', 'Device information', 'Usage behavior'] },
              { type: 'paragraph', text: 'Google may process this information according to its own privacy policies.' },
              { type: 'paragraph', text: "Users can opt out of Google Analytics through Google's official browser add-on." },
              {
                type: 'paragraph',
                text: 'We may also work with advertising partners who use cookies, pixels, and related technologies to deliver relevant advertisements.',
              },
            ],
          },
          {
            type: 'group',
            title: 'Information from Third Parties',
            blocks: [
              { type: 'paragraph', text: 'We may receive information from:' },
              { type: 'list', items: ['Syndication Partners', 'Developers', 'Resellers', 'Marketing partners', 'Publicly available sources'] },
              { type: 'paragraph', text: 'This information may include:' },
              { type: 'list', items: ['Organizational details', 'System identifiers', 'Business profile information'] },
              { type: 'paragraph', text: 'We only receive such information where legally permitted.' },
            ],
          },
        ],
      },
    ],
  },
  {
    title: '2. How We Use Your Information',
    blocks: [
      { type: 'paragraph', text: 'We use collected information for the following purposes:' },
      {
        type: 'group',
        title: 'Service Delivery',
        blocks: [
          { type: 'paragraph', text: 'To:' },
          { type: 'list', items: ['Provide and operate our Services', 'Authenticate users', 'Maintain accounts', 'Deliver requested features', 'Facilitate integrations'] },
        ],
      },
      {
        type: 'group',
        title: 'Product Improvement & Analytics',
        blocks: [
          { type: 'paragraph', text: 'To:' },
          { type: 'list', items: ['Understand usage trends', 'Improve user experience', 'Develop new features', 'Measure service performance', 'Conduct research and analysis'] },
        ],
      },
      {
        type: 'group',
        title: 'Personalization',
        blocks: [
          { type: 'paragraph', text: 'To:' },
          { type: 'list', items: ['Customize user experiences', 'Remember preferences', 'Deliver relevant content', 'Improve recommendations'] },
        ],
      },
      {
        type: 'group',
        title: 'Communications',
        blocks: [
          { type: 'paragraph', text: 'To:' },
          {
            type: 'list',
            items: [
              'Respond to inquiries',
              'Provide customer support',
              'Send service notifications',
              'Deliver account updates',
              'Inform users of security matters',
            ],
          },
        ],
      },
      {
        type: 'group',
        title: 'Marketing',
        blocks: [
          { type: 'paragraph', text: 'Where permitted by law, we may use your information to:' },
          {
            type: 'list',
            items: ['Send newsletters', 'Share product updates', 'Provide promotional communications', 'Inform you about new services'],
          },
          { type: 'paragraph', text: 'You may unsubscribe at any time.' },
        ],
      },
    ],
  },
  {
    title: '3. Legal Basis for Processing (EEA Users)',
    blocks: [
      {
        type: 'paragraph',
        text: 'For users located within the European Economic Area (EEA), we process personal information under one or more of the following legal bases:',
      },
      {
        type: 'list',
        items: ['Your consent', 'Performance of a contract', 'Compliance with legal obligations', 'Legitimate business interests', 'Protection of vital interests'],
      },
      { type: 'paragraph', text: 'Where consent is required, you may withdraw it at any time.' },
    ],
  },
  {
    title: '4. Information Sharing and Disclosure',
    blocks: [
      { type: 'paragraph', text: 'AppGallop does not sell personal information to third parties.' },
      { type: 'paragraph', text: 'We may share information in the following circumstances:' },
      {
        type: 'group',
        title: 'Developers & Syndication Partners',
        blocks: [{ type: 'paragraph', text: 'To enable integrations and functionality provided through the AppGallop Marketplace.' }],
      },
      {
        type: 'group',
        title: 'Service Providers',
        blocks: [
          { type: 'paragraph', text: 'With trusted third-party providers who assist us with:' },
          { type: 'list', items: ['Hosting', 'Maintenance', 'Technical support', 'Analytics', 'Business operations'] },
          { type: 'paragraph', text: 'These providers may access information only as necessary to perform services on our behalf.' },
        ],
      },
      {
        type: 'group',
        title: 'Other Users',
        blocks: [{ type: 'paragraph', text: 'Information shared publicly through profiles, comments, reviews, or messages may be visible to other users.' }],
      },
      {
        type: 'group',
        title: 'Aggregated Information',
        blocks: [{ type: 'paragraph', text: 'We may share anonymized or aggregated information that cannot reasonably identify individuals.' }],
      },
      {
        type: 'group',
        title: 'Legal Compliance',
        blocks: [
          { type: 'paragraph', text: 'We may disclose information when required to:' },
          { type: 'list', items: ['Comply with legal obligations', 'Respond to lawful requests', 'Protect our rights', 'Investigate fraud or misuse', 'Maintain security'] },
        ],
      },
      {
        type: 'group',
        title: 'Business Transfers',
        blocks: [
          { type: 'paragraph', text: 'Information may be transferred during:' },
          { type: 'list', items: ['Mergers', 'Acquisitions', 'Asset sales', 'Corporate restructuring'] },
        ],
      },
    ],
  },
  {
    title: '5. International Data Transfers',
    blocks: [
      { type: 'paragraph', text: 'Your information may be transferred to and processed in countries outside your country of residence, including India.' },
      { type: 'paragraph', text: 'Where required by applicable law, we implement appropriate safeguards, including:' },
      { type: 'list', items: ['Standard Contractual Clauses (SCCs)', 'Data processing agreements', 'Security controls and compliance measures'] },
    ],
  },
  {
    title: '6. Your Privacy Rights',
    blocks: [
      { type: 'paragraph', text: 'Subject to applicable laws, you may have the right to:' },
      {
        type: 'list',
        items: [
          'Access your personal information',
          'Correct inaccurate information',
          'Delete personal information',
          'Restrict processing',
          'Object to processing',
          'Request data portability',
          'Withdraw consent',
          'Lodge complaints with regulatory authorities',
        ],
      },
      { type: 'paragraph', text: 'To exercise your rights, contact us using the details provided below.' },
    ],
  },
  {
    title: '7. Third-Party Services',
    blocks: [
      { type: 'paragraph', text: 'Our Services may contain links to third-party websites, applications, and services.' },
      {
        type: 'paragraph',
        text: 'We are not responsible for the privacy practices of third parties. We encourage users to review the privacy policies of those services before providing personal information.',
      },
    ],
  },
  {
    title: '8. Data Security',
    blocks: [
      {
        type: 'paragraph',
        text: 'We implement appropriate administrative, technical, and physical safeguards designed to protect personal information from:',
      },
      { type: 'list', items: ['Unauthorized access', 'Loss', 'Misuse', 'Alteration', 'Disclosure'] },
      {
        type: 'paragraph',
        text: 'While we strive to protect your information, no security measure is completely foolproof, and we cannot guarantee absolute security.',
      },
    ],
  },
  {
    title: '9. Data Retention',
    blocks: [
      { type: 'paragraph', text: 'We retain personal information only as long as necessary to:' },
      {
        type: 'list',
        items: ['Provide Services', 'Fulfill contractual obligations', 'Comply with legal requirements', 'Resolve disputes', 'Enforce agreements'],
      },
      {
        type: 'paragraph',
        text: 'When information is no longer required, it will be securely deleted, anonymized, or archived in accordance with applicable laws.',
      },
    ],
  },
  {
    title: '10. Updates to This Privacy Notice',
    blocks: [
      { type: 'paragraph', text: 'We may update this Privacy Notice periodically to reflect changes in:' },
      { type: 'list', items: ['Legal requirements', 'Business practices', 'Service offerings', 'Technology'] },
      {
        type: 'paragraph',
        text: 'Updated versions will be published on our Services along with the effective date. Continued use of the Services after such updates constitutes acceptance of the revised Notice.',
      },
    ],
  },
  {
    title: '11. Contact Us',
    blocks: [
      {
        type: 'paragraph',
        text: 'If you have questions, concerns, requests, or complaints regarding this Privacy Notice or our data practices, please contact us:',
      },
      { type: 'paragraph', text: 'AppGallop Pvt. Ltd.' },      
      { type: 'paragraph', text: 'Website: www.appgallop.com' },
      {
        type: 'paragraph',
        text: 'We will make reasonable efforts to respond to your inquiries promptly and in accordance with applicable privacy laws.',
      },
    ],
  },
];

const ContentRenderer = ({ blocks }: { blocks: ContentBlock[] }) => (
  <div className="space-y-5">
    {blocks.map((block, index) => {
      if (block.type === 'paragraph') {
        return (
          <p key={index} className="text-sm font-medium leading-7 text-slate-600">
            {block.text}
          </p>
        );
      }

      if (block.type === 'list') {
        return (
          <ul key={index} className="grid gap-2 pl-5 text-sm font-medium leading-7 text-slate-600">
            {block.items.map((item) => (
              <li key={item} className="list-disc marker:text-[#ff5d22]">
                {item}
              </li>
            ))}
          </ul>
        );
      }

      return (
        <div key={index} className="space-y-3">
          <h3 className="text-base font-black text-slate-950">{block.title}</h3>
          <ContentRenderer blocks={block.blocks} />
        </div>
      );
    })}
  </div>
);

export default function PrivacyPolicyPage() {
  return (
    <main className="bg-white pt-28 text-slate-950">
      <section className="mx-auto max-w-4xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="mb-10">
          <p className="mb-4 text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">
            AppGallop
          </p>
          <h1 className="text-4xl font-black tracking-tight md:text-5xl">Privacy Notice</h1>
        </div>

        <div className="mb-12 rounded-lg border border-slate-200 bg-slate-50 p-6">
          <h2 className="mb-4 text-xl font-black">Introduction</h2>
          <div className="space-y-4">
            {intro.map((paragraph) => (
              <p key={paragraph} className="text-sm font-medium leading-7 text-slate-600">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="space-y-12">
          {sections.map((section) => (
            <article key={section.title} className="border-t border-slate-200 pt-8">
              <h2 className="mb-5 text-2xl font-black tracking-tight text-slate-950">
                {section.title}
              </h2>
              <ContentRenderer blocks={section.blocks} />
            </article>
          ))}
        </div>

        <div className="mt-12 border-t border-slate-200 pt-6">
          <Link
            to="/"
            className="inline-flex min-h-11 items-center rounded-lg bg-[#ff5d22] px-5 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-slate-950"
          >
            Back to Home
          </Link>
        </div>
      </section>
    </main>
  );
}
